import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  cardBodyRecipe,
  cardFooterRecipe,
  cardHeaderRecipe,
  surfaceRecipe,
  type SurfaceRecipeProps,
} from '../recipes/surface.recipe';
import { Text } from './Text';

export interface SurfaceProps
  extends React.HTMLAttributes<HTMLDivElement>,
    SurfaceRecipeProps {}

/**
 * A raised panel with no opinion about its contents.
 *
 * Use this when you need a container's look — border, radius, shadow, tint —
 * without Card's header/body/footer structure.
 */
export const Surface = React.forwardRef<HTMLDivElement, SurfaceProps>(
  function Surface(
    {
      className,
      elevation,
      bordered,
      radius,
      padding,
      interactive,
      tone,
      accent,
      ...props
    },
    ref
  ) {
    return (
      <div
        ref={ref}
        className={cn(
          surfaceRecipe({
            elevation,
            bordered,
            radius,
            padding,
            interactive,
            tone,
            accent,
          }),
          className
        )}
        {...props}
      />
    );
  }
);

type CardPadding = 'none' | 'sm' | 'md' | 'lg';

export interface CardProps extends Omit<SurfaceProps, 'padding'> {
  /**
   * Padding applied to CardHeader / CardBody / CardFooter, so the three stay
   * on the same inset without each having to be told.
   */
  padding?: CardPadding;
}

const CardPaddingContext = React.createContext<CardPadding>('lg');

/**
 * The standard panel: a Surface plus a shared padding scale for its parts.
 *
 *   <Card>
 *     <CardHeader title="Open calls" description="Updated a moment ago" />
 *     <CardBody>…</CardBody>
 *   </Card>
 */
export const Card = React.forwardRef<HTMLDivElement, CardProps>(function Card(
  { padding = 'lg', children, ...props },
  ref
) {
  return (
    <CardPaddingContext.Provider value={padding}>
      <Surface ref={ref} padding="none" {...props}>
        {children}
      </Surface>
    </CardPaddingContext.Provider>
  );
});

export interface CardHeaderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** Buttons or a menu, aligned to the right of the title. */
  actions?: React.ReactNode;
  /** Draw a rule under the header, making it a bar rather than a floating title. */
  divided?: boolean;
  /** `muted` tints the header into a band across the top of the card. */
  tone?: 'default' | 'muted';
  /** `center` for a card that is a message — an invitation, a confirmation. */
  align?: 'start' | 'center';
}

/**
 * Title block for a Card, in either of two shapes.
 *
 * Pass `title` / `description` / `actions` and it lays out the standard header
 * — text on the left, actions on the right:
 *
 *   <CardHeader title="Open calls" actions={<Button>New</Button>} />
 *
 * Pass children instead and it is a plain column you fill yourself, which is
 * what <CardTitle> / <CardDescription> are for:
 *
 *   <CardHeader>
 *     <CardTitle>Open calls</CardTitle>
 *   </CardHeader>
 *
 * The composed form renders children directly rather than nesting them in a
 * wrapper, so `className` on this element really is the element wrapping them.
 * ~50 headers in the app position things against it.
 */
export const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  function CardHeader(
    {
      title,
      description,
      actions,
      divided,
      tone,
      align,
      className,
      children,
      ...props
    },
    ref
  ) {
    const padding = React.useContext(CardPaddingContext);
    const composed =
      title === undefined && description === undefined && actions === undefined;

    if (composed) {
      return (
        <div
          ref={ref}
          className={cn(
            cardHeaderRecipe({ padding, divided, tone, align }),
            // A column rather than the title/actions row, and tighter — these
            // are lines of one text block, not two separate regions.
            'flex-col gap-1.5',
            className
          )}
          {...props}
        >
          {children}
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={cn(
          cardHeaderRecipe({ padding, divided, tone, align }),
          className
        )}
        {...props}
      >
        {/* Centred text needs the column to span the row; shrink-wrapped, it
            would sit at the left edge with its lines centred inside it. */}
        <div
          className={cn(
            'relay-ui-internal min-w-0 space-y-1',
            align === 'center' && 'flex-1'
          )}
        >
          {title ? <Text variant="h3">{title}</Text> : null}
          {description ? (
            <Text variant="body-sm" tone="muted">
              {description}
            </Text>
          ) : null}
          {children}
        </div>
        {actions ? (
          <div className="relay-ui-internal flex shrink-0 items-center gap-2">
            {actions}
          </div>
        ) : null}
      </div>
    );
  }
);

export interface CardBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Override the Card's inset for this part alone. `none` is the one that
   * matters: a table or a list that should run to the card's edges while the
   * header above it keeps its padding. Five cards wrote this as `p-0`.
   */
  padding?: CardPadding;
}

export const CardBody = React.forwardRef<HTMLDivElement, CardBodyProps>(
  function CardBody({ className, padding, ...props }, ref) {
    const inherited = React.useContext(CardPaddingContext);
    return (
      <div
        ref={ref}
        className={cn(
          cardBodyRecipe({ padding: padding ?? inherited }),
          className
        )}
        {...props}
      />
    );
  }
);

export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Draw a rule above the footer. */
  divided?: boolean;
}

export const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  function CardFooter({ className, divided, ...props }, ref) {
    const padding = React.useContext(CardPaddingContext);
    return (
      <div
        ref={ref}
        className={cn(
          cardFooterRecipe({ padding, divided }),
          divided && 'pt-4',
          className
        )}
        {...props}
      />
    );
  }
);

/**
 * CardTitle / CardDescription / CardContent
 *
 * The composed way to fill a Card:
 *
 *   <Card>
 *     <CardHeader>
 *       <CardTitle>Open calls</CardTitle>
 *       <CardDescription>Updated a moment ago</CardDescription>
 *     </CardHeader>
 *     <CardContent>…</CardContent>
 *   </Card>
 *
 * which is the same thing CardHeader's `title` / `description` props do. Both
 * exist because the app was written against the composed shape and has ~60
 * cards using it; the props form is shorter and preferred for new code.
 *
 * One difference worth knowing: CardTitle is `h4` (16px) where the `title`
 * prop is `h3` (18px). CardTitle matches what those ~60 cards already render,
 * so adopting it changes nothing on screen. Reconciling the two is a
 * consolidation job, not a migration one — see docs/library-migration.md.
 */
export const CardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(function CardTitle({ className, ...props }, ref) {
  return (
    <Text
      ref={ref as React.Ref<HTMLElement>}
      as="h3"
      variant="h4"
      className={className}
      {...props}
    />
  );
});

export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(function CardDescription({ className, ...props }, ref) {
  return (
    <Text
      ref={ref as React.Ref<HTMLElement>}
      as="p"
      variant="body-sm"
      tone="muted"
      className={className}
      {...props}
    />
  );
});

/** The composed name for CardBody. Identical behaviour. */
export const CardContent = CardBody;
